import { useHubspotForm } from "@aaronhayes/react-use-hubspot-form";
import AnchorLink from "@bmi-digital/components/anchor-link";
import Checkbox from "@bmi-digital/components/checkbox";
import Form, { FormContext } from "@bmi-digital/components/form";
import Grid from "@bmi-digital/components/grid";
import ArrowForwardIcon from "@bmi-digital/components/icon/ArrowForward";
import Section from "@bmi-digital/components/section";
import Select, {
  MenuItem as SelectMenuItem
} from "@bmi-digital/components/select";
import TextField from "@bmi-digital/components/text-field";
import Upload, { getFileSizeString } from "@bmi-digital/components/upload";
import { replaceSpaces } from "@bmi-digital/components/utils";
import logger from "@bmi-digital/functions-logger";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import classNames from "classnames";
import { graphql, navigate } from "gatsby";
import uniqueId from "lodash-es/uniqueId";
import fetch from "node-fetch";
import React, {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { QA_AUTH_TOKEN } from "../constants/cookieConstants";
import { useConfig } from "../contexts/ConfigProvider";
import {
  handleEmailValidation as validateEmail,
  isValidEmail
} from "../utils/emailUtils";
import getCookie from "../utils/getCookie";
import { getPathWithCountryCode } from "../utils/path";
import ControlledCheckboxGroup from "./CheckboxGroup";
import ProgressIndicator from "./ProgressIndicator";
import RecaptchaPrivacyLinks from "./RecaptchaPrivacyLinks";
import RichText from "./RichText";
import { useSiteContext } from "./Site";
import { isExternalUrl } from "./link/utils";
import {
  classes,
  HubspotFormWrapper,
  MarkdownLinksWrapper,
  StyledForm
} from "./styles/FormSectionStyles";
import type { RichTextData } from "./RichText";
import type { InputValue } from "@bmi-digital/components/form";
import type {
  PayloadFormCheckboxGroupInput,
  PayloadFormCheckboxInput,
  PayloadFormEmailInput,
  PayloadFormPhoneInput,
  PayloadFormSection,
  PayloadFormSelectInput,
  PayloadFormTextAreaInput,
  PayloadFormTextInput,
  PayloadFormUploadInput
} from "../types/payload";
import type { InternalLinkBlock } from "./link/types";
import type { GTM } from "@bmi-digital/components";

export type FormTextInput = Pick<
  PayloadFormTextInput,
  "blockType" | "label" | "name" | "width" | "required"
>;

export type FormEmailInput = Pick<
  PayloadFormEmailInput,
  "blockType" | "label" | "name" | "width" | "required"
>;

export type FormPhoneInput = Pick<
  PayloadFormPhoneInput,
  "blockType" | "label" | "name" | "width" | "required"
>;

export type FormTextAreaInput = Pick<
  PayloadFormTextAreaInput,
  "blockType" | "label" | "name" | "width" | "required"
>;

export type FormCheckboxInput = Pick<
  PayloadFormCheckboxInput,
  "blockType" | "label" | "name" | "width" | "required"
>;

export type FormCheckboxGroupInput = Pick<
  PayloadFormCheckboxGroupInput,
  "blockType" | "label" | "name" | "options" | "width" | "required"
>;

export type FormSelectInput = Pick<
  PayloadFormSelectInput,
  "blockType" | "label" | "name" | "options" | "width" | "required"
>;

export type FormUploadInput = Pick<
  PayloadFormUploadInput,
  "blockType" | "label" | "name" | "accept" | "maxSize" | "width" | "required"
>;

export type FormInputsData =
  | FormTextInput
  | FormEmailInput
  | FormPhoneInput
  | FormTextAreaInput
  | FormCheckboxInput
  | FormCheckboxGroupInput
  | FormSelectInput
  | FormUploadInput;

export type FormData = Pick<
  PayloadFormSection,
  | "title"
  | "showTitle"
  | "recipients"
  | "submitText"
  | "source"
  | "hubSpotFormGuid"
  | "emailSubjectFormat"
> & {
  richTextDescription: RichTextData | null;
  inputs: readonly FormInputsData[] | null;
  successRedirect: readonly [InternalLinkBlock] | never[];
};

export type FormSectionData = FormData & Pick<PayloadFormSection, "blockType">;

const BYTES_IN_ONE_MEGABYTE = 1048576;

export const convertMarkdownLinksToAnchorLinks = (
  source?: string
): React.ReactNode => {
  if (!source) {
    return;
  }

  const matches = [...source.matchAll(/\[([^\]]+)\]\(([^)]+)\)/g)];

  if (!matches || !matches.length) {
    return source;
  }

  if (matches.length === 1) {
    const [match, label, link] = matches[0];
    const { index: offset, input } = matches[0];

    return (
      <>
        {input!.substring(0, offset)}
        <AnchorLink
          href={link}
          external={isExternalUrl(link) || undefined}
          data-testid={`label-${replaceSpaces(label)}-anchor-link`}
          gtm={{
            id: "cta-click1",
            label,
            action: link
          }}
        >
          {label}
        </AnchorLink>
        {input!.substring(offset! + match.length)}
      </>
    );
  }

  let lastIndex = 0;
  const parts = matches.filter(Boolean).map((el, index) => {
    const [full, label, url] = el;
    const { index: offset, input } = el;

    const result = (
      <React.Fragment key={index}>
        {/* text before this match */}
        <span>{input!.slice(lastIndex, offset)}</span>
        {/* reconstructed markdown link */}
        <AnchorLink
          href={url}
          external={isExternalUrl(url)}
          data-testid={`label-${replaceSpaces(label)}-anchor-link`}
        >
          {label}
        </AnchorLink>
      </React.Fragment>
    );

    lastIndex = offset! + full.length;

    return result;
  });

  const remainingText = matches[0]!.input!.slice(lastIndex);

  return (
    <MarkdownLinksWrapper>
      {parts}
      {/* append remaining text */}
      {remainingText ? <span>{remainingText}</span> : null}
    </MarkdownLinksWrapper>
  );
};

const Input = (formInputsData: FormInputsData & { formId?: string }) => {
  const { gcpFormUploadEndpoint } = useConfig();
  const { getMicroCopy } = useSiteContext();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const qaAuthToken = getCookie(QA_AUTH_TOKEN);
  const { updateFormState } = useContext(FormContext);

  const maxSizeInBytes = useMemo(() => {
    if ("maxSize" in formInputsData && formInputsData.maxSize) {
      return formInputsData.maxSize * BYTES_IN_ONE_MEGABYTE;
    }
    return null;
  }, [formInputsData]);

  const mapValue = ({ name, type }, upload) => ({
    fileName: name,
    contentType: type,
    uploadFrom: {
      url: upload.url,
      sys: {
        type: "Link",
        linkType: "Upload",
        /**
         * upload.sys.id - Contentful
         * upload.id - Payload
         */
        id: upload.sys.id || upload.id
      }
    }
  });

  const handleEmailValidation = useCallback(
    (value: string) =>
      validateEmail(getMicroCopy(microCopy.ERRORS_EMAIL_INVALID), value),
    [getMicroCopy]
  );

  const validateFileSize = useCallback(
    (file: File): string | undefined => {
      if (maxSizeInBytes && file.size > maxSizeInBytes) {
        return getMicroCopy(microCopy.ERRORS_MAX_SIZE, {
          size: getFileSizeString(maxSizeInBytes)
        });
      }
      return undefined;
    },
    [maxSizeInBytes, getMicroCopy]
  );

  const handleFileValidation = useCallback(
    (file: File) => validateFileSize(file),
    [validateFileSize]
  );

  const handleFilesChange = useCallback(
    (files: File[]) => {
      let error: string | undefined;

      for (const file of files) {
        error = validateFileSize(file);

        if (error) {
          break;
        }
      }

      updateFormState({}, { [formInputsData.name]: error });
    },
    [validateFileSize, updateFormState, formInputsData.name]
  );

  const onUploadRequest = useCallback(
    async (file: File) => {
      const token = qaAuthToken ? undefined : await executeRecaptcha?.();
      let headers: HeadersInit = {
        "X-Recaptcha-Token": token,
        "X-FileName": file.name
      };
      if (qaAuthToken) {
        headers = { ...headers, authorization: `Bearer ${qaAuthToken}` };
      }

      return { headers: { ...headers } };
    },
    [executeRecaptcha, qaAuthToken]
  );

  switch (formInputsData.blockType) {
    case "upload-input": {
      const acceptTypes = formInputsData.accept.join(",");
      return (
        <Upload
          id={`${formInputsData.formId}-${formInputsData.name}`}
          formId={formInputsData.formId}
          name={formInputsData.name}
          buttonLabel={formInputsData.label}
          isRequired={formInputsData.required}
          fieldIsRequiredError={getMicroCopy(
            microCopy.UPLOAD_FIELD_IS_REQUIRED
          )}
          uri={gcpFormUploadEndpoint}
          headers={{
            "Content-Type": "application/octet-stream"
          }}
          accept={acceptTypes}
          fileValidation={handleFileValidation}
          onFilesChange={handleFilesChange}
          instructions={
            maxSizeInBytes
              ? `${getMicroCopy(
                  microCopy.FORM_UPLOAD_SUPPORTED_FORMATS
                )}: ${acceptTypes}.\n${getMicroCopy(
                  microCopy.FORM_UPLOAD_MAX_SIZE
                )}: ${getFileSizeString(maxSizeInBytes)}`
              : undefined
          }
          mapValue={mapValue}
          onUploadRequest={onUploadRequest}
          microcopyProvider={{
            "upload.instructions.drop": getMicroCopy(
              microCopy.UPLOAD_INSTRUCTIONS_DROP
            ),
            "upload.instructions.browse": getMicroCopy(
              microCopy.UPLOAD_INSTRUCTIONS_BROWSE
            )
          }}
        />
      );
    }
    case "select-input":
      return (
        <Select
          isRequired={formInputsData.required}
          fieldIsRequiredError={getMicroCopy(
            microCopy.UPLOAD_FIELD_IS_REQUIRED
          )}
          label={formInputsData.label}
          name={formInputsData.name}
        >
          {formInputsData.options?.map((option, $i) => {
            const [select, value] = option.split(/= |=/);
            return (
              <SelectMenuItem key={$i} value={value ? option : select}>
                {select}
              </SelectMenuItem>
            );
          })}
        </Select>
      );
    case "checkbox-input":
      return (
        <Checkbox
          name={formInputsData.name}
          label={convertMarkdownLinksToAnchorLinks(formInputsData.label)}
          isRequired={formInputsData.required}
          fieldIsRequiredError={getMicroCopy(
            microCopy.UPLOAD_FIELD_IS_REQUIRED
          )}
        />
      );
    case "checkbox-group-input":
      return (
        <ControlledCheckboxGroup
          name={formInputsData.name}
          options={formInputsData.options}
          groupName={convertMarkdownLinksToAnchorLinks(formInputsData.label)}
          isRequired={Boolean(formInputsData.required)}
          fieldIsRequiredError={getMicroCopy(
            microCopy.UPLOAD_FIELD_IS_REQUIRED
          )}
        />
      );
    case "text-area-input":
    case "text-input":
    default:
      return (
        <TextField
          id={formInputsData.name}
          name={formInputsData.name}
          isRequired={formInputsData.required}
          errorText={getMicroCopy(microCopy.UPLOAD_FIELD_IS_REQUIRED)}
          fieldIsRequiredError={getMicroCopy(
            microCopy.UPLOAD_FIELD_IS_REQUIRED
          )}
          isTextArea={formInputsData.blockType === "text-area-input"}
          variant="outlined"
          label={formInputsData.label}
          fullWidth
          {...(formInputsData.blockType === "text-area-input" && { rows: 6 })}
          {...(formInputsData.blockType === "email-input" && {
            getValidationError: handleEmailValidation
          })}
        />
      );
  }
};

type FormInputs = {
  formId?: string;
  inputs: readonly FormInputsData[];
};

export const FormInputs = ({ formId, inputs }: FormInputs) => (
  <>
    {inputs.map((input) => (
      <Grid
        key={`${formId}-${input.name}`}
        xs={12}
        md={input.width === "full" ? 12 : 6}
      >
        <Input
          formId={formId}
          data-testid={`form-input-${input.name}`}
          {...input}
        />
      </Grid>
    ))}
  </>
);

const HubspotForm = ({
  id,
  hubSpotFormGuid,
  backgroundColor,
  showTitle,
  title,
  description,
  onSuccess,
  onFormReady,
  onFormLoadError,
  additionalValues,
  className,
  hasNoPadding,
  isDialog = false,
  customDescriptionElement
}: {
  id: string;
  hubSpotFormGuid: string;
  backgroundColor: "pearl" | "white";
  showTitle: boolean | null;
  title?: string;
  description?: RichTextData;
  onSuccess: FormSectionProps["onSuccess"];
  onFormReady?: FormSectionProps["onFormReady"];
  onFormLoadError?: FormSectionProps["onFormLoadError"];
  additionalValues: FormSectionProps["additionalValues"];
  className?: string;
  isDialog?: boolean;
  hasNoPadding?: boolean;
  customDescriptionElement?: React.ReactElement;
}) => {
  const { hubSpotId } = useConfig();
  const hubSpotFormID = useMemo(() => {
    if (id.length > 0) {
      return `bmi-hubspot-form-${id}`;
    }

    return `bmi-hubspot-form-${uniqueId(replaceSpaces(title))}`;
  }, [id, title]);

  // Uses the HS script to bring in the form. This will create an iframe regardless
  // of styling options, but will only _use_ the iframe if it's _not_ raw HTML (empty
  // iframe otherwise).
  useHubspotForm({
    portalId: hubSpotId || "",
    formId: hubSpotFormGuid,
    target: `#${hubSpotFormID}`
  });

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data.type === "hsFormCallback" &&
        event.data.eventName === "onFormReady"
      ) {
        const iframeElement = document.querySelector<HTMLIFrameElement>(
          `#${hubSpotFormID} iframe`
        );
        const hubspotform = document.querySelector<HTMLFormElement>(
          `#${hubSpotFormID} form`
        );

        if (additionalValues && additionalValues["samples"]) {
          const sampleIdsInput = document.querySelector<HTMLInputElement>(
            'input[name="sample_ids"]'
          );

          if (sampleIdsInput) {
            sampleIdsInput.value = additionalValues["samples"];
          } else {
            const hiddenInput =
              iframeElement?.contentWindow?.document.querySelector<HTMLInputElement>(
                'input[name="sample_ids"]'
              );
            hiddenInput && (hiddenInput.value = additionalValues["samples"]);
          }
        }
        if (isDialog) {
          onFormReady?.(event, hubspotform);
        } else {
          onFormReady?.(event, iframeElement);
        }
      }

      if (event.data.eventName === "onFormSubmitted") {
        onSuccess?.();
      }

      if (event.data.eventName === "onFormDefinitionFetchError") {
        onFormLoadError?.();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("message", handleMessage);
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    additionalValues,
    hubSpotFormID,
    isDialog,
    onFormLoadError,
    onFormReady,
    onSuccess
  ]);

  return (
    <Section
      backgroundColor={backgroundColor}
      className={className}
      isDialog={isDialog}
      hasNoPadding={hasNoPadding}
      data-testid={`hubspot-form-section-${replaceSpaces(title)}`}
    >
      {showTitle && <Section.Title>{title}</Section.Title>}
      {customDescriptionElement ? (
        customDescriptionElement
      ) : (
        <RichText document={description} />
      )}
      <HubspotFormWrapper
        data-testid={`hubspot-form-${replaceSpaces(title)}`}
        id={hubSpotFormID}
        className={classNames(isDialog && classes.dialog)}
      />
    </Section>
  );
};

type FormSectionProps = {
  id: string;
  data: FormData;
  backgroundColor: "pearl" | "white";
  additionalValues?: Record<string, string>;
  sampleIds?: string;
  isSubmitDisabled?: boolean;
  gtmOverride?: { action?: GTM["action"]; label?: GTM["label"] };
  onSuccess?: () => void;
  onFormReady?: (
    event: MessageEvent,
    hsForm?: HTMLIFrameElement | HTMLFormElement
  ) => void;
  onFormLoadError?: () => void;
  className?: string;
  isDialog?: boolean;
  hasNoPadding?: boolean;
  customDescriptionElement?: React.ReactElement;
  "data-testid"?: string;
};

const FormSection = ({
  id,
  data: {
    title,
    showTitle = true,
    richTextDescription,
    recipients,
    inputs,
    submitText,
    successRedirect,
    source,
    hubSpotFormGuid,
    emailSubjectFormat
  },
  backgroundColor,
  additionalValues,
  isSubmitDisabled,
  gtmOverride,
  onSuccess,
  onFormReady,
  onFormLoadError,
  className,
  hasNoPadding,
  isDialog = false,
  customDescriptionElement,
  "data-testid": dataTestId
}: FormSectionProps) => {
  const { isPreviewMode, gcpFormSubmitEndpoint, hubspotApiUrl, hubSpotId } =
    useConfig();
  const { countryCode, getMicroCopy, node_locale } = useSiteContext();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const qaAuthToken = getCookie(QA_AUTH_TOKEN);

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
    values: Record<string, InputValue>
  ) => {
    event.preventDefault();

    if (isPreviewMode) {
      alert("You cannot submit a form on a preview environment.");
      return;
    }

    setIsSubmitting(true);

    Object.assign(values, additionalValues);
    const recipientsFromValues = (values.recipients as string) || "";
    const isEmailPresent = ["@", "="].every((char) =>
      recipientsFromValues.includes(char)
    );
    const recipientEmail = recipientsFromValues.split(/= |=/)[1];
    if (!isValidEmail(recipientEmail)) {
      console.error("Error", "invalid e-mail recipient address"); // eslint-disable-line
    }
    const conditionalRecipients =
      recipientsFromValues && isEmailPresent ? recipientEmail : recipients;

    try {
      const token = qaAuthToken ? undefined : await executeRecaptcha?.();

      // remove all blank values
      const valuesToSent = Object.entries(values).reduce(
        (acc, [key, value]) => {
          if (Array.isArray(value) && !value.length) {
            return acc;
          }
          return value ? ((acc[`${key}`] = value), acc) : acc;
        },
        {}
      );

      let headers: HeadersInit = {
        "Content-Type": "application/json",
        "X-Recaptcha-Token": token
      };
      if (qaAuthToken) {
        headers = { ...headers, authorization: `Bearer ${qaAuthToken}` };
      }
      const response = await fetch(gcpFormSubmitEndpoint, {
        method: "POST",
        body: JSON.stringify({
          locale: node_locale,
          title,
          recipients: conditionalRecipients,
          values: valuesToSent,
          emailSubjectFormat
        }),
        headers
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      onSuccess && onSuccess();

      if (successRedirect.length) {
        navigate(
          getPathWithCountryCode(
            countryCode,
            successRedirect[0].linkedPage.value.path
          )
        );
      } else {
        navigate("/");
      }
    } catch (error) {
      logger.error({ message: (error as Error).message });
    }

    setIsSubmitting(false);
  };

  //This function right now is not used. Left here only for future extend of Hubspot usage
  const handleHubSpotSubmit = async (
    event: FormEvent<HTMLFormElement>,
    values: Record<string, InputValue>
  ) => {
    event.preventDefault();

    if (isPreviewMode) {
      alert("You cannot submit a form on a preview environment.");
      return;
    }

    setIsSubmitting(true);

    const valuesArray = Object.entries(values).map(([name, value]) => ({
      name,
      value
    }));

    const valuesWithoutLegaConsent = valuesArray.filter(
      (field) => field.name.substring(0, 9) !== "hs-legal-"
    );

    const hsLegalFields = valuesArray
      .filter((field) => field.name.substring(0, 9) === "hs-legal-")
      .reduce((acc, cur) => {
        return { ...acc, [cur.name]: cur.value };
      }, {});

    const hsPayload = {
      fields: valuesWithoutLegaConsent,
      context: {
        pageUri: window.location.href
      }
    };

    const getLegalOptions = (hsLegalFields: { [key: string]: InputValue }) => {
      if (hsLegalFields["hs-legal-isLegitimateInterest"]) {
        return {
          legitimateInterest: {
            value: true,
            subscriptionTypeId: hsLegalFields["hs-legal-communicationTypeId"],
            legalBasis: "LEAD",
            text: hsLegalFields["hs-legal-privacyPolicyText"]
          }
        };
      }
      if (hsLegalFields["hs-legal-communication"]) {
        return {
          consent: {
            consentToProcess:
              hsLegalFields["hs-legal-processingConsentType"] || true,
            text: hsLegalFields["hs-legal-processingConsentText"],
            communications: [
              {
                value: hsLegalFields["hs-legal-communication"],
                subscriptionTypeId:
                  hsLegalFields["hs-legal-communicationTypeId"],
                text: hsLegalFields["hs-legal-communicationConsentText"]
              }
            ]
          }
        };
      }
    };

    try {
      const response = await fetch(
        `${hubspotApiUrl}${hubSpotId}/${hubSpotFormGuid}`,
        {
          method: "POST",
          body: JSON.stringify({
            ...hsPayload,
            ...(hsLegalFields
              ? {
                  legalConsentOptions: getLegalOptions(hsLegalFields)
                }
              : {})
          }),
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      if (successRedirect) {
        navigate(
          getPathWithCountryCode(
            countryCode,
            successRedirect[0].linkedPage.value.path
          )
        );
      } else {
        navigate("/");
      }
    } catch (error) {
      logger.error({ message: (error as Error).message });
    }

    setIsSubmitting(false);
  };

  if (source === "HubSpot" && hubSpotFormGuid) {
    return (
      <HubspotForm
        id={id}
        hubSpotFormGuid={hubSpotFormGuid}
        backgroundColor={backgroundColor}
        showTitle={showTitle && !isDialog}
        title={title}
        description={richTextDescription}
        onSuccess={onSuccess}
        onFormReady={onFormReady}
        onFormLoadError={onFormLoadError}
        additionalValues={additionalValues}
        className={className}
        isDialog={isDialog}
        hasNoPadding={hasNoPadding}
        customDescriptionElement={customDescriptionElement}
        data-testid={dataTestId}
      />
    );
  }
  return (
    <Section
      backgroundColor={backgroundColor}
      className={className}
      hasNoPadding={hasNoPadding}
      data-testid={`payload-form-section-${replaceSpaces(title)}`}
    >
      {showTitle && <Section.Title>{title}</Section.Title>}
      {richTextDescription && <RichText document={richTextDescription} />}
      {inputs ? (
        <StyledForm
          onSubmit={
            // TODO Handle/remove after HubSpot mapping has been decided
            source === "HubSpot" && hubSpotFormGuid
              ? handleHubSpotSubmit
              : handleSubmit
          }
          rightAlignButton
          data-testid={dataTestId}
        >
          <Grid container spacing={3}>
            <FormInputs formId={id} inputs={inputs} />
          </Grid>
          <Form.ButtonWrapper>
            <Form.SubmitButton
              gtm={{
                id: "form-button1",
                action: gtmOverride?.action ? gtmOverride.action : title,
                label: gtmOverride?.label ? gtmOverride.label : "children"
              }}
              endIcon={
                isSubmitting ? (
                  <ProgressIndicator
                    size={24}
                    color="inherit"
                    style={{
                      position: "relative",
                      left: 0,
                      right: 0,
                      marginLeft: "0.5rem"
                    }}
                  />
                ) : (
                  <ArrowForwardIcon />
                )
              }
              disabled={isSubmitting || isSubmitDisabled}
              data-testid={`payload-form-section-${replaceSpaces(
                title
              )}-submit-button`}
            >
              {submitText || getMicroCopy(microCopy.FORM_SUBMIT)}
            </Form.SubmitButton>
          </Form.ButtonWrapper>
        </StyledForm>
      ) : (
        "Form contains no fields"
      )}

      <RecaptchaPrivacyLinks />
    </Section>
  );
};
export default FormSection;

export const query = graphql`
  fragment FormDataFragment on PayloadFormSection {
    title
    showTitle
    richTextDescription: description {
      ...RichTextFragment
    }
    recipients
    inputs {
      ...FormInputsFragment
    }
    submitText
    successRedirect {
      ...InternalLinkBlockFragment
    }
    source
    hubSpotFormGuid
    emailSubjectFormat
  }
  fragment FormSectionFragment on PayloadFormSection {
    ...FormDataFragment
    blockType
  }
  fragment FormInputsFragment on PayloadFormInputs {
    ... on PayloadFormTextInput {
      blockType
      label
      name
      width
      required
    }
    ... on PayloadFormEmailInput {
      blockType
      label
      name
      width
      required
    }
    ... on PayloadFormPhoneInput {
      blockType
      label
      name
      width
      required
    }
    ... on PayloadFormTextAreaInput {
      blockType
      label
      name
      width
      required
    }
    ... on PayloadFormCheckboxInput {
      blockType
      label
      name
      width
      required
    }
    ... on PayloadFormCheckboxGroupInput {
      blockType
      label
      name
      options
      width
      required
    }
    ... on PayloadFormSelectInput {
      blockType
      label
      name
      options
      width
      required
    }
    ... on PayloadFormUploadInput {
      blockType
      label
      name
      accept
      maxSize
      width
      required
    }
  }
  fragment FormDataFragmentNonRecursive on PayloadFormSection {
    blockType
    title
    showTitle
    richTextDescription: description {
      ...RichTextFragmentNonRecursive
    }
    recipients
    inputs {
      ...FormInputsFragment
    }
    submitText
    successRedirect {
      ...InternalLinkBlockFragment
    }
    source
    hubSpotFormGuid
  }
`;
